<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useRouter, useRoute } from 'vue-router';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

import Policy from 'dashboard/components/policy.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SearchHeader from './SearchHeader.vue';
import SearchTabs from './SearchTabs.vue';
import SearchResultConversationsList from './SearchResultConversationsList.vue';
import SearchResultMessagesList from './SearchResultMessagesList.vue';
import SearchResultContactsList from './SearchResultContactsList.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const selectedTab = ref('all');
const query = ref('');
const pages = ref({
  contacts: 1,
  conversations: 1,
  messages: 1,
});
const recentMessagesPage = ref(1);

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const contactRecords = useMapGetter('conversationSearch/getContactRecords');
const conversationRecords = useMapGetter(
  'conversationSearch/getConversationRecords'
);
const messageRecords = useMapGetter('conversationSearch/getMessageRecords');
const recentMessages = useMapGetter('conversationSearch/getRecentMessages');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const addTypeToRecords = (records, type) =>
  records.value.map(item => ({ ...item, type }));

const mappedContacts = computed(() =>
  addTypeToRecords(contactRecords, 'contact')
);
const mappedConversations = computed(() =>
  addTypeToRecords(conversationRecords, 'conversation')
);
const mappedMessages = computed(() =>
  addTypeToRecords(messageRecords, 'message')
);
const mappedRecentMessages = computed(() =>
  addTypeToRecords(recentMessages, 'message')
);

const isSelectedTabAll = computed(() => selectedTab.value === 'all');

const sliceRecordsIfAllTab = items =>
  isSelectedTabAll.value ? items.value.slice(0, 5) : items.value;

const contacts = computed(() => sliceRecordsIfAllTab(mappedContacts));
const conversations = computed(() => sliceRecordsIfAllTab(mappedConversations));
const messages = computed(() => sliceRecordsIfAllTab(mappedMessages));

const filterByTab = tab =>
  computed(() => selectedTab.value === tab || isSelectedTabAll.value);

const filterContacts = filterByTab('contacts');
const filterConversations = filterByTab('conversations');
const filterMessages = filterByTab('messages');

const userPermissions = computed(() =>
  getUserPermissions(currentUser.value, currentAccountId.value)
);

const TABS_CONFIG = {
  all: {
    permissions: [CONTACT_PERMISSIONS, ...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => null, // No count for all tab
  },
  contacts: {
    permissions: [...ROLES, CONTACT_PERMISSIONS],
    count: () => mappedContacts.value.length,
  },
  conversations: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedConversations.value.length,
  },
  messages: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedMessages.value.length,
  },
};

const tabs = computed(() => {
  const configs = Object.entries(TABS_CONFIG).map(([key, config]) => ({
    key,
    name: t(`SEARCH.TABS.${key.toUpperCase()}`),
    count: config.count(),
    showBadge: key !== 'all',
    permissions: config.permissions,
  }));

  return filterItemsByPermission(
    configs,
    userPermissions.value,
    item => item.permissions
  );
});

const totalSearchResultsCount = computed(() => {
  const permissionCounts = {
    contacts: {
      permissions: [...ROLES, CONTACT_PERMISSIONS],
      count: () => contacts.value.length,
    },
    conversations: {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      count: () => conversations.value.length + messages.value.length,
    },
  };
  return filterItemsByPermission(
    permissionCounts,
    userPermissions.value,
    item => item.permissions,
    (_, item) => item.count
  ).reduce((total, count) => total + count(), 0);
});

const activeTabIndex = computed(() => {
  const index = tabs.value.findIndex(tab => tab.key === selectedTab.value);
  return index >= 0 ? index : 0;
});

const isFetchingAny = computed(() => {
  const { contact, message, conversation, isFetching } = uiFlags.value;
  return (
    isFetching ||
    contact.isFetching ||
    message.isFetching ||
    conversation.isFetching
  );
});

const showEmptySearchResults = computed(
  () =>
    totalSearchResultsCount.value === 0 &&
    uiFlags.value.isSearchCompleted &&
    isSelectedTabAll.value &&
    !isFetchingAny.value &&
    query.value
);

const showResultsSection = computed(
  () =>
    (uiFlags.value.isSearchCompleted && totalSearchResultsCount.value !== 0) ||
    isFetchingAny.value ||
    (!isSelectedTabAll.value && query.value && !isFetchingAny.value)
);

const showLoadMore = computed(() => {
  if (!query.value) {
    // recent messages
    return (
      !uiFlags.value.recentMessages.isFetching &&
      mappedRecentMessages.value.length > 0 &&
      Boolean(uiFlags.value.recentMessages.hasMore)
    );
  }

  if (isFetchingAny.value || selectedTab.value === 'all') return false;

  const records = {
    contacts: mappedContacts.value,
    conversations: mappedConversations.value,
    messages: mappedMessages.value,
  }[selectedTab.value];

  // hasMore comes from the raw API page size; stored record counts shrink
  // when overlapping pages are deduped, so they cannot signal more pages
  const hasMore = {
    contacts: uiFlags.value.contact.hasMore,
    conversations: uiFlags.value.conversation.hasMore,
    messages: uiFlags.value.message.hasMore,
  }[selectedTab.value];

  return records?.length > 0 && Boolean(hasMore);
});

const showViewMore = computed(() => ({
  // Hide view more button if the number of records is less than 5
  contacts: mappedContacts.value?.length > 5 && isSelectedTabAll.value,
  conversations:
    mappedConversations.value?.length > 5 && isSelectedTabAll.value,
  messages: mappedMessages.value?.length > 5 && isSelectedTabAll.value,
}));

const clearSearchResult = () => {
  pages.value = { contacts: 1, conversations: 1, messages: 1 };
  recentMessagesPage.value = 1;
  store.dispatch('conversationSearch/clearSearchResults');
};

const onSearch = q => {
  query.value = q;
  clearSearchResult();

  const newQuery = { ...route.query };
  if (q) {
    newQuery.q = q;
  } else {
    delete newQuery.q;
  }
  router.replace({ query: newQuery });

  if (!q) {
    store.dispatch('conversationSearch/recentMessages');
    return;
  }
  useTrack(CONVERSATION_EVENTS.SEARCH_CONVERSATION);
};

const onBack = () => {
  if (window.history.length > 2) {
    router.go(-1);
  } else {
    router.push({ name: 'home' });
  }
  clearSearchResult();
};

const loadMore = async () => {
  if (!query.value) {
    if (uiFlags.value.recentMessages.isFetching) return;
    recentMessagesPage.value += 1;
    const success = await store.dispatch('conversationSearch/recentMessages', {
      page: recentMessagesPage.value,
    });
    // Roll back on failure so a retry fetches the same page instead of
    // skipping past the one that never loaded
    if (!success) recentMessagesPage.value -= 1;
    return;
  }

  const SEARCH_ACTIONS = {
    contacts: 'conversationSearch/contactSearch',
    conversations: 'conversationSearch/conversationSearch',
    messages: 'conversationSearch/messageSearch',
  };

  if (uiFlags.value.isFetching || selectedTab.value === 'all') return;
  const tab = selectedTab.value;
  pages.value[tab] += 1;
  const success = await store.dispatch(SEARCH_ACTIONS[tab], {
    q: query.value,
    page: pages.value[tab],
  });
  if (!success) pages.value[tab] -= 1;
};

onMounted(() => {
  const urlQuery = route.query.q;
  const urlTab = route.query.tab;

  if (
    urlTab &&
    ['all', 'contacts', 'conversations', 'messages'].includes(urlTab)
  ) {
    selectedTab.value = urlTab;
  }

  if (urlQuery) {
    query.value = urlQuery;
    store.dispatch('conversationSearch/fullSearch', { q: urlQuery, page: 1 });
  } else {
    store.dispatch('conversationSearch/clearSearchResults');
    store.dispatch('conversationSearch/recentMessages');
  }
});

// Watch for URL query parameter changes (e.g., browser back/forward)
watch(
  () => route.query.q,
  (newQuery, oldQuery) => {
    if (newQuery !== oldQuery) {
      if (newQuery) {
        query.value = newQuery;
        clearSearchResult();
        store.dispatch('conversationSearch/fullSearch', {
          q: newQuery,
          page: 1,
        });
      } else {
        query.value = '';
        clearSearchResult();
        store.dispatch('conversationSearch/recentMessages');
      }
    }
  }
);

watch(
  () => route.query.tab,
  newTab => {
    if (
      newTab &&
      ['all', 'contacts', 'conversations', 'messages'].includes(newTab)
    ) {
      selectedTab.value = newTab;
    }
  }
);

// Watch for selectedTab changes and update URL
watch(selectedTab, newTab => {
  const newQuery = { ...route.query };
  if (newTab && newTab !== 'all') {
    newQuery.tab = newTab;
  } else {
    delete newQuery.tab;
  }
  router.replace({ query: newQuery });
});

onUnmounted(() => {
  if (!route.query.q) {
    store.dispatch('conversationSearch/clearSearchResults');
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div class="flex w-full p-4">
      <NextButton
        :label="t('GENERAL_SETTINGS.BACK')"
        icon="i-lucide-chevron-left"
        faded
        primary
        sm
        @click="onBack"
      />
    </div>
    <section class="flex flex-col flex-grow w-full h-full overflow-hidden">
      <div class="w-full max-w-4xl mx-auto">
        <div class="flex flex-col w-full px-4">
          <SearchHeader :initial-query="query" @search="onSearch" />
          <SearchTabs
            v-if="query"
            :tabs="tabs"
            :selected-tab="activeTabIndex"
            @tab-change="tab => (selectedTab = tab)"
          />
        </div>
      </div>
      <div class="flex-grow w-full h-full overflow-y-auto">
        <div class="w-full max-w-4xl mx-auto px-4 pb-6">
          <div v-if="showResultsSection">
            <Policy
              :permissions="[...ROLES, CONTACT_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultContactsList
                v-if="filterContacts"
                :is-fetching="uiFlags.contact.isFetching"
                :contacts="contacts"
                :query="query"
                :show-title="isSelectedTabAll"
              />
              <NextButton
                v-if="showViewMore.contacts"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'contacts'"
              />
            </Policy>

            <Policy
              :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultMessagesList
                v-if="filterMessages"
                :is-fetching="uiFlags.message.isFetching"
                :messages="messages"
                :query="query"
                :show-title="isSelectedTabAll"
              />
              <NextButton
                v-if="showViewMore.messages"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'messages'"
              />
            </Policy>

            <Policy
              :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
              class="flex flex-col justify-center"
            >
              <SearchResultConversationsList
                v-if="filterConversations"
                :is-fetching="uiFlags.conversation.isFetching"
                :conversations="conversations"
                :query="query"
                :show-title="isSelectedTabAll"
              />
              <NextButton
                v-if="showViewMore.conversations"
                :label="t(`SEARCH.VIEW_MORE`)"
                icon="i-lucide-eye"
                slate
                sm
                outline
                @click="selectedTab = 'conversations'"
              />
            </Policy>

            <div v-if="showLoadMore" class="flex justify-center mt-4 mb-6">
              <NextButton
                v-if="!isSelectedTabAll"
                :label="t(`SEARCH.LOAD_MORE`)"
                icon="i-lucide-cloud-download"
                slate
                sm
                faded
                @click="loadMore"
              />
            </div>
          </div>
          <div
            v-else-if="showEmptySearchResults"
            class="flex flex-col items-center justify-center px-4 py-6 mt-8 rounded-md"
          >
            <fluent-icon icon="info" size="16px" class="text-n-slate-11" />
            <p class="m-2 text-center text-n-slate-11">
              {{ t('SEARCH.EMPTY_STATE_FULL', { query }) }}
            </p>
          </div>
          <div v-else-if="!query">
            <SearchResultMessagesList
              v-if="
                mappedRecentMessages.length || uiFlags.recentMessages.isFetching
              "
              :is-fetching="uiFlags.recentMessages.isFetching"
              :messages="mappedRecentMessages"
              query=""
              show-title
              :title="t('SEARCH.RECENT_MESSAGES')"
            />
            <div v-if="showLoadMore" class="flex justify-center mt-4 mb-6">
              <NextButton
                :label="t('SEARCH.LOAD_MORE')"
                icon="i-lucide-cloud-download"
                slate
                sm
                faded
                @click="loadMore"
              />
            </div>
            <div
              v-if="
                !mappedRecentMessages.length &&
                !uiFlags.recentMessages.isFetching
              "
              class="flex flex-col items-center justify-center px-4 py-6 mt-8 text-center rounded-md"
            >
              <p class="text-center margin-bottom-0">
                <fluent-icon
                  icon="search"
                  size="24px"
                  class="text-n-slate-11"
                />
              </p>
              <p class="m-2 text-center text-n-slate-11">
                {{ t('SEARCH.EMPTY_STATE_DEFAULT') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
